/**
 * @file schemas/site-schedule.ts
 * @description 現場稽核「多日行程」共用契約與純轉換函式（2026-09-25 回報序74/75）。
 *
 * 背景：稽核計畫表單（D-ZHSR02-29 / D-ZHSR02-3）與稽核排程計劃（audit_tasks.schedule_json）
 * 原本的行程表每一行都要填「日期」。客戶要求：
 *  1. 行程列移除「日期」，改由所屬的「現場稽核日期」繼承；
 *  2. 支援多日稽核：可新增多組「現場稽核日期 + 當日行程」，每組各自獨立；
 *  3. 提供「載入範例行程」，範例只預填當前組。
 *
 * 新資料形狀（表單 dailySchedule 欄位值 / schedule_json.siteDays 共用）：
 *   SiteScheduleDay[] = [{ date: 'YYYY-MM-DD' | '', rows: SiteScheduleRow[] }]
 *
 * 舊資料相容（只讀轉換，不改寫舊資料）：
 *  - 舊表單：頂層 site_audit_date + schedule_table（每行可含 date）→ 依行日期分組；
 *  - 舊排程：schedule_json.days = [{ date, startTime, endTime, activity, site, participants }]
 *    （每行一筆、各自帶日期）→ 依日期分組，時段 = 起~訖。
 */
/** 行程列欄位 id（順序即表格欄位順序） */
export const SITE_SCHEDULE_ROW_KEYS = [
    'time',
    'activity',
    'location',
    'group',
    'accompany',
];
/** 多日行程上限（防異常資料撐爆表單） */
export const SITE_SCHEDULE_MAX_DAYS = 31;
/** 單日行程列上限 */
export const SITE_SCHEDULE_MAX_ROWS_PER_DAY = 30;
/**
 * 範例行程（依客戶原表 D 5ZHSR02-29 Rev4 第 4 頁「稽核活動行程（範例）」）。
 * 產銷履歷原表範例僅列活動骨架（時間留空），同樣沿用此時段安排作為範例，載入後可自由修改。
 */
export const SAMPLE_SITE_SCHEDULE_ROWS = {
    TOP: [
        {
            time: '09:30~09:50',
            activity: '稽核首次會議',
            location: '管理中心（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
        {
            time: '09:55~11:50',
            activity: '加工現場稽核、產品採樣送檢',
            location: '生產廠（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
        { time: '12:00~13:00', activity: '午餐' },
        {
            time: '13:00~15:30',
            activity: '文件稽核',
            location: '生產廠（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
        {
            time: '15:30~16:30',
            activity: '編寫稽核報告',
            location: '管理中心（稽核小組）',
            group: '稽核小組',
        },
        {
            time: '16:30~17:00',
            activity: '稽核總結會議',
            location: '管理中心（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
    ],
    TAP: [
        {
            time: '09:30~09:50',
            activity: '稽核首次會議',
            location: '管理中心（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
        {
            time: '09:55~11:50',
            activity: '生產現場稽核、產品採樣送檢',
            location: '生產場區（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
        { time: '12:00~13:00', activity: '午餐' },
        {
            time: '13:00~15:30',
            activity: '產銷履歷資訊系統及文件紀錄稽核',
            location: '管理中心（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
        {
            time: '15:30~16:30',
            activity: '編寫稽核報告',
            location: '管理中心（稽核小組）',
            group: '稽核小組',
        },
        {
            time: '16:30~17:00',
            activity: '稽核總結會議',
            location: '管理中心（驗證管理代表、製程操作人員及稽核小組）',
            group: '稽核小組',
        },
    ],
};
/**
 * 取範例行程（回傳深拷貝，呼叫端可直接修改）。
 * @param scheme 驗證方案；TAP 用產銷履歷範例，其餘回落有機範例
 */
export function getSampleSiteScheduleRows(scheme) {
    const src = scheme === 'TAP' ? SAMPLE_SITE_SCHEDULE_ROWS.TAP : SAMPLE_SITE_SCHEDULE_ROWS.TOP;
    return src.map((r) => ({ ...r }));
}
/** 未知值 → trim 後字串；非標量 / 空白回 undefined */
function toStr(v) {
    if (v == null)
        return undefined;
    if (typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean')
        return undefined;
    const s = String(v).trim();
    return s === '' ? undefined : s;
}
/**
 * 日期正規化為 YYYY-MM-DD：
 *  - 'YYYY-MM-DD' 原樣；
 *  - ISO datetime（舊表單 date 欄位存 toISOString()）→ 取台灣時區日期；
 *  - 其他 / 空 → ''。
 */
export function toScheduleDate(v) {
    const s = toStr(v);
    if (!s)
        return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s))
        return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime()))
        return '';
    return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
}
/** 行程列是否有任何內容（全空列視為無效） */
export function siteScheduleRowHasContent(row) {
    if (!row)
        return false;
    return SITE_SCHEDULE_ROW_KEYS.some((k) => toStr(row[k]) !== undefined);
}
/** 把未知行物件正規化為 SiteScheduleRow（只保留 5 列、丟棄空值） */
function normalizeRow(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const r = raw;
    const row = {};
    for (const k of SITE_SCHEDULE_ROW_KEYS) {
        const v = toStr(r[k]);
        if (v !== undefined)
            row[k] = v;
    }
    return row;
}
/**
 * 依日期把「每行帶日期」的扁平行程分組為多日結構（保留首次出現順序；無日期行歸入空日期組）。
 * @param flat 扁平行：{ date, ...SiteScheduleRow }
 */
export function groupFlatScheduleRows(flat) {
    const order = [];
    const byDate = new Map();
    for (const item of flat) {
        const date = toScheduleDate(item.date);
        const { date: _omit, ...rest } = item;
        void _omit;
        if (!byDate.has(date)) {
            byDate.set(date, []);
            order.push(date);
        }
        byDate.get(date).push(rest);
    }
    // 有日期的組按日期升序，無日期組排最後
    const dated = order.filter((d) => d !== '').sort();
    const result = dated.map((date) => ({ date, rows: byDate.get(date) }));
    if (byDate.has(''))
        result.push({ date: '', rows: byDate.get('') });
    return result.slice(0, SITE_SCHEDULE_MAX_DAYS);
}
/**
 * 正規化任意來源的行程值為 SiteScheduleDay[]（讀取端容錯入口）。
 *
 * 接受：
 *  1. 新形狀：[{ date, rows: [...] }]；
 *  2. 舊表單 schedule_table 扁平行：[{ date?, time, activity, location, group, accompany }]；
 *  3. 舊排程 schedule_json.days：[{ date, startTime, endTime, activity, site, participants }]。
 * 丟棄全空列；丟棄「無日期且無行程」的空組；截斷至上限。
 *
 * @param raw 未知原始值
 * @param fallbackDate 扁平行未帶日期時使用的日期（舊表單頂層 site_audit_date）
 */
export function normalizeSiteScheduleDays(raw, fallbackDate) {
    if (!Array.isArray(raw) || raw.length === 0)
        return [];
    const items = raw.filter((x) => !!x && typeof x === 'object');
    const isNewShape = items.some((x) => Array.isArray(x.rows));
    if (isNewShape) {
        const out = [];
        for (const d of items) {
            const rows = (Array.isArray(d.rows) ? d.rows : [])
                .map(normalizeRow)
                .filter((r) => r != null && siteScheduleRowHasContent(r))
                .slice(0, SITE_SCHEDULE_MAX_ROWS_PER_DAY);
            const date = toScheduleDate(d.date);
            if (!date && rows.length === 0)
                continue;
            out.push({ date, rows });
            if (out.length >= SITE_SCHEDULE_MAX_DAYS)
                break;
        }
        return out;
    }
    // 扁平行（舊表單或舊排程）
    const fallback = toScheduleDate(fallbackDate);
    const flat = [];
    for (const r of items) {
        const isLegacyPlan = 'startTime' in r || 'endTime' in r || 'site' in r || 'participants' in r;
        const row = isLegacyPlan
            ? {
                time: toStr([toStr(r.startTime), toStr(r.endTime)].filter(Boolean).join('~')),
                activity: toStr(r.activity),
                location: toStr(r.site),
                group: toStr(r.participants),
            }
            : (normalizeRow(r) ?? {});
        const date = toScheduleDate(r.date) || fallback;
        // 全空列略過；但舊排程「只有日期」的行仍保留日期組（行程留空待補）
        if (!siteScheduleRowHasContent(row) && !date)
            continue;
        flat.push({ ...stripEmpty(row), date });
    }
    return groupFlatScheduleRows(flat).map((d) => ({
        date: d.date,
        rows: d.rows.filter(siteScheduleRowHasContent).slice(0, SITE_SCHEDULE_MAX_ROWS_PER_DAY),
    }));
}
/** 移除值為 undefined 的鍵（保持輸出乾淨，便於比較 / 序列化） */
function stripEmpty(row) {
    const out = {};
    for (const k of SITE_SCHEDULE_ROW_KEYS) {
        if (row[k] !== undefined)
            out[k] = row[k];
    }
    return out;
}
/**
 * 多日行程攤平為「每行帶日期」的扁平行（供仍使用單一 table 行程欄位的舊版表單導入）。
 */
export function flattenSiteScheduleDays(days) {
    const out = [];
    for (const d of days) {
        if (d.rows.length === 0) {
            if (d.date)
                out.push({ date: d.date });
            continue;
        }
        for (const r of d.rows)
            out.push({ date: d.date || undefined, ...r });
    }
    return out;
}
/**
 * 由預計稽核日期組出多日骨架（每個日期一組、行程留空），供查無行程時回退。
 * 去重、升序、截斷。
 */
export function siteScheduleDaysFromDates(dates) {
    const set = new Set();
    for (const d of dates) {
        const s = toScheduleDate(d);
        if (s)
            set.add(s);
    }
    return [...set]
        .sort()
        .slice(0, SITE_SCHEDULE_MAX_DAYS)
        .map((date) => ({ date, rows: [] }));
}
